import { opacityModifier } from '@/utils';
import { Color, Dist, Font, Shadows, Theme, TransitionStyles } from './theme.types';

const background: string = `linear-gradient(112.07deg, rgba(225, 247, 254, 0.8) -0.19%, rgba(47, 0, 181, 0.8) 99.7%),
radial-gradient(23.69% 37.41% at 99.06% 0%, rgba(254, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0) 100%),
linear-gradient(0deg, #FFFFFF, #FFFFFF),
radial-gradient(47.69% 118.91% at 97.69% 96.89%, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%)`;

const fontFamily: { [key: string]: string } = {
  roboto: 'Roboto, sans-serif',
  poppins: 'Poppins, sans-serif',
};

const color: Color = {
  ui1: 'rgba(11,11,12,1)',
  ui2: 'rgba(50,53,56,1)',
  ui3: 'rgba(130,130,130,1)',
  ui4: 'rgba(196,196,196,1)',
  ui5: 'rgba(237,237,237,1)',
  ui6: 'rgba(247,247,247,1)',
  ui7: 'rgba(255,255,255,1)',
  ui8: 'rgba(247,247,247,0.7)',
  s1: 'rgba(78,15,210,1)',
  s2: 'rgba(113,66,255,1)',
  ac11: 'rgba(255,62,108,1)',
  ac12: 'rgba(255,98,135,1)',
  ac13: 'rgba(255,217,226,1)',
  ac21: 'rgba(255,145,0,1)',
  ac22: 'rgba(255,179,64,1)',
  ac23: 'rgba(255,219,166,1)',
  ac31: 'rgba(72,191,48,1)',
  ac32: 'rgba(117,217,98,1)',
  ac33: 'rgba(192,242,182,1)',
  ac41: 'rgba(48,127,226,1)',
  ac42: 'rgba(63,169,245,1)',
  ac43: 'rgba(181,228,255,1)',
  t1: 'rgba(255,70,70,1)',
  t2: 'rgba(255,160,20,1)',
  t3: 'rgba(250,220,40,1)',
  t4: 'rgba(100,200,80,1)',
  t5: 'rgba(30,180,200,1)',
  t6: 'rgba(70, 80, 250,1)',
  t7: 'rgba(180,70,240,1)',
  t8: 'rgba(255,160,150,1)',
  t9: 'rgba(255,200,120,1)',
  t10: 'rgba(255,240,160,1)',
  t11: 'rgba(150,230,200,1)',
  t12: 'rgba(110,220,240,1)',
  t13: 'rgba(170, 170, 255,1)',
  t14: 'rgba(230,180,250,1)',
  t15: 'rgba(160,0,0,1)',
  t16: 'rgba(150,70,0,1)',
  t17: 'rgba(190,170,30,1)',
  t18: 'rgba(60,120,40,1)',
  t19: 'rgba(40,100,140,1)',
  t20: 'rgba(30, 40, 140,1)',
  t21: 'rgba(100,30,140,1)',
  transparent: 'rgba(0,0,0,0)',
};

const dist: Dist = {
  xxxs: '2px',
  xxs: '4px',
  xs: '8px',
  s: '12px',
  sm: '16px',
  ms: '20px',
  m: '24px',
  l: '32px',
  xl: '50px',
};

const font: Font = {
  t1: {
    fontSize: '20px',
    fontWeight: '700',
    fontFamily: fontFamily.poppins,
    lineHeight: '24px',
    letterSpacing: '0em',
  },
  t2: {
    fontSize: '14px',
    fontWeight: '400',
    fontFamily: fontFamily.roboto,
    lineHeight: '20px',
    letterSpacing: '0em',
  },
  t3: {
    fontSize: '14px',
    fontWeight: '500',
    fontFamily: fontFamily.roboto,
    lineHeight: '20px',
    letterSpacing: '0em',
  },
  t4: {
    fontSize: '11px',
    fontWeight: '400',
    fontFamily: fontFamily.roboto,
    lineHeight: '16px',
    letterSpacing: '0em',
  },
  t5: {
    fontSize: '11px',
    fontWeight: '500',
    fontFamily: fontFamily.roboto,
    lineHeight: '20px',
    letterSpacing: '0em',
  },
  t6: {
    fontSize: '9px',
    fontWeight: '400',
    fontFamily: fontFamily.roboto,
    lineHeight: '12px',
    letterSpacing: '0em',
  },
  t7: {
    fontSize: '6px',
    fontWeight: '400',
    fontFamily: fontFamily.roboto,
    lineHeight: '7px',
    letterSpacing: '0em',
  },
};

// TODO(omer): Add the rest of the values for radius. E.g. xs, sm, lg..
const radius = {
  xs: '4px',
  sm: '8px',
  m: '16px',
  lg: '20px',
};

// TODO(omer): The color usage for shadows seems confusing, think of a better way to pick colors for shadows
const shadowColors = {
  color0: 'rgba(255, 255, 255, 1)',
  color1: 'rgba(0, 203, 203, 1)',
  color2: 'rgba(78, 15, 210, 1)',
  color3: 'rgba(247, 81, 250, 1)',
  color4: 'rgba(93, 56, 169, 1)',
  color5: 'rgba(3, 16, 62, 1)',
  color6: 'rgba(69, 248, 216, 1)',
  color7: 'rgba(11, 11, 12, 1)',
};

const transition: TransitionStyles = {
  default: {
    'transition-property': 'all',
    'transition-timing-function': 'cubic-bezier(0.4, 0, 0.2, 1)',
    'transition-duration': '150ms',
  },
};

const shadows: Shadows = {
  outerShadow1: /* Figma: Level1 outerShadow1 */ `
    0px 1px 1px ${opacityModifier(shadowColors.color7, 0.2)}`,
  outerShadow: /* Figma: Level2 outerShadow2 */ `
    1px 1px 1px ${opacityModifier(shadowColors.color6, 0.2)},
    2px 2px 4px ${opacityModifier(shadowColors.color4, 0.2)},
    inset 1px 1px 1px ${opacityModifier(shadowColors.color3, 0.05)},
    inset 3px 3px 4px ${opacityModifier(shadowColors.color0, 0.3)}`,
  outerShadow2: /* Figma: Level2 outerShadow2 */ `
      1px 1px 1px ${opacityModifier(shadowColors.color6, 0.2)},
      2px 2px 4px ${opacityModifier(shadowColors.color4, 0.2)},
      inset 1px 1px 1px ${opacityModifier(shadowColors.color3, 0.05)},
      inset 3px 3px 4px ${opacityModifier(shadowColors.color0, 0.3)}`,
  innerShadow3: /* Figma: Level-1A innerShadow3 */ `
    inset -1px -1px 2px ${opacityModifier(shadowColors.color3, 0.05)},
    inset 2px 2px 2px ${opacityModifier(shadowColors.color2, 0.05)},
    inset -2px -2px 2px ${opacityModifier(shadowColors.color0, 0.3)}`,
  innerShadow2: /* Figma: Level-1B innerShadow2 */ `
    inset -2px -2px 2px ${opacityModifier(shadowColors.color0, 0.05)},
    inset 2px 2px 2px ${opacityModifier(shadowColors.color5, 0.3)}`,
  innerShadow: /* Figma: Level-2 innerShadow1 */ `
    inset -1px -1px 4px ${opacityModifier(shadowColors.color3, 0.1)},
    inset 1px 1px 2px ${opacityModifier(shadowColors.color6, 0.2)},
    inset 4px 4px 4px ${opacityModifier(shadowColors.color2, 0.1)},
    inset -4px -4px 4px ${opacityModifier(shadowColors.color0, 0.5)};`,
  defaultShadow: /* DEPRECATED */ '0 4px 4px rgba(0,0,0,0.25)',
  innerShadowDark: /* DEPRECATED */ `inset -1px -1px 4px ${opacityModifier(
    shadowColors.color3,
    0.1,
  )}, inset 1px 1px 2px ${opacityModifier(
    shadowColors.color6,
    0.2,
  )}, inset 4px 4px 4px ${opacityModifier(
    shadowColors.color2,
    0.1,
  )}, inset -4px -4px 4px ${opacityModifier(shadowColors.color0, 0.5)};`,
  viewportInnerShadow: /* DEPRECATED */ `
    inset -1px -1px 4px  ${opacityModifier(shadowColors.color3, 0.1)},
    inset 1px 1px 2px ${opacityModifier(shadowColors.color4, 0.2)},
    inset 4px 4px 4px ${opacityModifier(shadowColors.color2, 0.1)},
    inset -4px -4px 4px ${opacityModifier(color.ui7, 0.5)};`,
  standByShadow: /* DEPRECATED */ `1px 1px 1px ${opacityModifier(shadowColors.color7, 0.2)}`,
  dialog: /* DEPRECATED */ `
      1px 1px 1px ${opacityModifier(shadowColors.color1, 0.2)},
      2px 2px 4px ${opacityModifier(shadowColors.color2, 0.2)},
      inset 1px 1px 1px ${opacityModifier(shadowColors.color3, 0.1)}`,
  hover: /* DEPRECATED */ `
      2px 2px 4px ${opacityModifier(shadowColors.color4, 0.2)},
      3px 3px 4px ${opacityModifier(color.ui7, 0.3)},
      1px 1px 1px ${opacityModifier(shadowColors.color6, 0.2)},
      1px 1px 1px ${opacityModifier(shadowColors.color3, 0.05)}`,
  active: /* DEPRECATED */ `
    inset -2px -2px 2px ${opacityModifier(color.ui8, 0.2)},
    inset 2px 2px 2px ${opacityModifier(shadowColors.color5, 0.2)}`,
  filterPrimary: /* DEPRECATED */ `drop-shadow(0px 0px 2px ${color.s2});`,
  fieldShadow: /* DEPRECATED */ `
    inset -1px -1px 2px ${opacityModifier(shadowColors.color3, 0.1)},
    inset 1px 1px 2px ${opacityModifier(shadowColors.color2, 0.1)},
    inset -2px -2px 2px ${opacityModifier(shadowColors.color0, 0.5)};`,
  inputInnerShadow: /* DEPRECATED */ `
    inset -1px -1px 2px ${opacityModifier(shadowColors.color3, 0.1)},
    inset 1px 1px 2px ${opacityModifier(color.s1, 0.1)},
    inset -2px -2px 2px ${opacityModifier(color.ui7, 0.5)}`,
  buttonDarkInnerShadow: /* DEPRECATED */ `
    inset -2px -2px 2px ${opacityModifier(shadowColors.color0, 0.2)},
    inset 2px 2px 2px ${opacityModifier(shadowColors.color5, 0.3)};`,
  tooltipInnerShadow: /* DEPRECATED */ `
    1px 1px 1px ${opacityModifier(shadowColors.color3, 0.05)},
    inset 3px 3px 4px ${opacityModifier(color.ui7, 0.3)}`,
  tooltipOuterShadow: /* DEPRECATED */ `
    drop-shadow(1px 1px 1px ${opacityModifier(shadowColors.color6, 0.2)})
    drop-shadow(2px 2px 4px ${opacityModifier(shadowColors.color4, 0.2)})`,
  sidebarButtonShadowHover: /* DEPRECATED */ `
    drop-shadow(-1px 1px 1px ${opacityModifier(shadowColors.color7, 0.2)})
  `,
  sidebarButtonShadow: /* DEPRECATED */ `
    drop-shadow(-1px 1px 1px ${opacityModifier(shadowColors.color6, 0.2)})
    drop-shadow(-6px 2px 4px ${opacityModifier(shadowColors.color4, 0.2)})
  `,
};

export const ThemeDefault = {
  background,
  color,
  dist,
  font,
  radius,
  shadow: opacityModifier(color.ui1, 0.25),
  shadows,
  transition,
} as Theme;
